import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const COUNTRIES = {
  BHD: { code: 'BHD', name: 'Bahrain', currency: 'BHD', symbol: 'BHD', country_iso: 'BHR', flag: '🇧🇭' },
  AED: { code: 'AED', name: 'United Arab Emirates', currency: 'AED', symbol: 'AED', country_iso: 'ARE', flag: '🇦🇪' },
  SAR: { code: 'SAR', name: 'Saudi Arabia', currency: 'SAR', symbol: 'SAR', country_iso: 'SAU', flag: '🇸🇦' },
  QAR: { code: 'QAR', name: 'Qatar', currency: 'QAR', symbol: 'QAR', country_iso: 'QAT', flag: '🇶🇦' },
  KWD: { code: 'KWD', name: 'Kuwait', currency: 'KWD', symbol: 'KWD', country_iso: 'KWT', flag: '🇰🇼' },
  OMR: { code: 'OMR', name: 'Oman', currency: 'OMR', symbol: 'OMR', country_iso: 'OMN', flag: '🇴🇲' },
  USD: { code: 'USD', name: 'Philippines', currency: 'USD', symbol: '$', country_iso: 'PHL', flag: '🇵🇭' },
};

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES.BHD);
  const [selectedCurrency, setSelectedCurrency] = useState(COUNTRIES.BHD.currency);
  const [demoData, setDemoData] = useState(null);
  const [discountAmt, setDiscountAmt] = useState(0);

  // Update the selected country and currency
  const updateCountry = (countryCode) => {
    const country = COUNTRIES[countryCode] || COUNTRIES.BHD;
    setSelectedCountry(country);
    setSelectedCurrency(country.currency);
  };

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('plsss_cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        setCart(parsedCart);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      localStorage.removeItem('plsss_cart');
    }
  }, []);

  // Save cart to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('plsss_cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const productCode = product.code ? String(product.code).trim() : String(product.id);
      const isUpgrade = !!product.isUpgrade;
      
      const existingItem = prevCart.find(item => item.id === productCode && !item.isGift);
      const price = product.price !== undefined ? parseFloat(product.price) : parseFloat(product.prices?.[selectedCurrency] || product.prices?.USD || 0);

      if (existingItem) {
        const newQuantity = (existingItem.quantity || 0) + quantity;
        if (newQuantity <= (product.stock || 999)) {
          return prevCart.map(item =>
            item.id === productCode && !item.isGift
              ? {
                ...item,
                price: price,
                quantity: newQuantity,
                isUpgrade: isUpgrade,
                upgradeFrom: product.upgradeFrom || item.upgradeFrom,
                upgradeFromOrder: product.upgradeFromOrder || item.upgradeFromOrder,
                upgradeFromOrderId: product.upgradeFromOrderId || item.upgradeFromOrderId
              }
              : item
          );
        }
        return prevCart;
      }

      return [
        ...prevCart,
        {
          id: productCode,
          name: product.name,
          price: price, 
          prices: product.prices || { USD: 0 }, 
          quantity: Math.min(quantity, product.stock || 1),
          stock: parseInt(product.stock) || 0,
          category: product.category,
          imageUrl: product.imageUrl || '',
          isGift: false,
          isUpgrade: isUpgrade,
          upgradeFrom: product.upgradeFrom || null,
          upgradeFromOrder: product.upgradeFromOrder || null,
          upgradeFromOrderId: product.upgradeFromOrderId || null
        }
      ];
    });
  };

  // Add as gift (price is always 0 for gifts)
  const addGift = (product, quantity = 1) => {
    setCart(prevCart => {
      const productCode = product.code ? String(product.code).trim() : String(product.id);
      const existingGift = prevCart.find(item => item.id === productCode && item.isGift);
      const price = 0; // Gifts are always free

      if (existingGift) {
        // increment gift quantity up to stock
        const newQty = Math.min((existingGift.quantity || 0) + quantity, product.stock || existingGift.quantity || 1);
        return prevCart.map(item =>
          item.id === productCode && item.isGift
            ? {
              ...item,
              price: price,
              quantity: newQty,
              paymentPeriod: product.paymentPeriod, // Preserve payment period
              paymentPlan: product.paymentPlan // Preserve payment plan data
            }
            : item
        );
      }

      return [
        ...prevCart,
        {
          id: productCode,
          name: product.name,
          price: price, // Always 0 for gifts
          prices: { [selectedCurrency]: 0, USD: 0 }, // Store prices as 0 for gifts
          quantity: Math.min(quantity, product.stock || 1),
          stock: parseInt(product.stock) || 0,
          category: product.category,
          imageUrl: product.imageUrl || '',
          isGift: true,
          paymentPeriod: product.paymentPeriod, // Preserve payment period from gift item
          paymentPlan: product.paymentPlan // Preserve payment plan data
        }
      ];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== String(productId)));
  };

  const removeGift = (productId) => {
    setCart(prevCart => prevCart.filter(item => !(item.id === String(productId) && item.isGift)));
  };

  const removeItem = (productId, isGift) => {
    setCart(prevCart => prevCart.filter(item => !(item.id === String(productId) && !!item.isGift === !!isGift)));
  };

  const updateQuantity = (productId, quantity) => {
    const newQuantity = parseInt(quantity);
    if (isNaN(newQuantity) || newQuantity < 1) return;

    setCart(prevCart =>
      prevCart.map(item =>
        item.id === String(productId)
          ? {
            ...item,
            quantity: newQuantity,
            prices: item.prices || { USD: item.price || 0 }
          }
          : item
      )
    );
  };

  const updateItemQuantity = (productId, isGift, quantity) => {
    const newQuantity = parseInt(quantity);
    if (isNaN(newQuantity) || newQuantity < 1) return;

    setCart(prevCart =>
      prevCart.map(item =>
        item.id === String(productId) && (!!item.isGift === !!isGift)
          ? {
            ...item,
            quantity: newQuantity,
            prices: item.prices || { USD: item.price || 0 }
          }
          : item
      )
    );
  };

  const updateDemoData = (data) => {
    setDemoData(data);
  };

  const clearCart = () => {
    setCart([]);
    setDiscountAmt(0);
  };

  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return !item.isGift ? total + ((item.price || 0) * (item.quantity || 0)) : total;
    }, 0);
  };

  const getDiscountedCartTotal = () => {
    const total = getCartTotal();
    return Math.max(0, total - discountAmt);
  };

  const getPaidItems = () => {
    return cart.filter(item => !item.isGift);
  };

  const getGiftItems = () => {
    return cart.filter(item => item.isGift);
  };

  const { paidItems, giftItems } = useMemo(() => {
    const paid = [];
    const gifts = [];

    cart.forEach(item => {
      if (item.isGift) {
        gifts.push(item);
      } else {
        paid.push(item);
      }
    });

    return { paidItems: paid, giftItems: gifts };
  }, [cart]);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        addGift,
        removeGift,
        updateItemQuantity,
        getCartTotal,
        getDiscountedCartTotal,
        discountAmt,
        setDiscountAmt,
        getCartCount,
        clearCart,
        paidItems,
        giftItems,
        selectedCountry,
        selectedCurrency,
        updateCountry,
        demoData,
        updateDemoData,
        COUNTRIES,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
